/**
 * LA PRESENTACIÓN DEL AGENTE EN LA HOME —
 * ADR-2026-09-22-la-barra-del-agente-es-la-presentacion-final.
 *
 * Los rubros SIN catálogo propio abren la home con el Agente desplegado
 * (`HeroAgenteHome`, ADR-2026-09-20-la-home-de-los-otros-rubros-abre-con-el-agente).
 * Los tres que SÍ lo tienen —Moda e Indumentaria, Belleza y Cuidado Personal, y Hogar y
 * Decoración— abren con sus modos, que son las herramientas armadas para ellos. Pero al
 * Agente hay que mostrárselo igual, y esta franja es esa presentación: el Agente **sin
 * desplegar**, como una puerta que se ve y se toca. No genera nada acá adentro —no hay
 * caja real, no hay conversación— y todo camino termina en el Agente con el pedido ya
 * escrito en su caja.
 *
 * ── UNA SOLA FORMA: LA BARRA ─────────────────────────────────────────────
 * Un rato se montaron tres a la vez (`barra`, `franja` y `tareas`) para compararlas en
 * la pantalla real, con el selector de dev y el `?agente-moda=` de la URL
 * (ADR-2026-09-21-la-home-de-moda-presenta-el-agente). Ganó LA BARRA y las otras dos se
 * borraron, que es lo que decía ese ADR que iba a pasar al elegir: una caja de texto
 * FALSA del ancho de la home, con el placeholder que se escribe solo y los pedidos abajo
 * como pastillas. Es la que menos explica y la que más invita: se entiende que se
 * escribe.
 *
 * ── EL COPY SIGUE AL RUBRO ───────────────────────────────────────────────
 * La franja nació para Moda y hablaba de prendas. Hoy la ven tres rubros, así que lo que
 * cambia con el rubro —la línea de presentación, los pedidos de las pastillas y las
 * ideas que se tipean solas— vive en `COPY_POR_RUBRO`. El resto (la caja, la estela, el
 * camino al Agente) es el mismo para todos: lo que se está mostrando es CÓMO se le habla
 * al Agente, y eso no cambia de rubro en rubro.
 *
 * ── MEDIR ────────────────────────────────────────────────────────────────
 * Dos eventos y nada más: `agente_home_visto` (una vez por montaje, con el rubro) y
 * `agente_home_click` (con el rubro y desde qué pieza se tocó).
 *
 * Es PRESENTACIONAL salvo por abrir el Agente, igual que `HeroAgenteHome`: el rubro y el
 * candado del plan entran por prop.
 */
import { useEffect, useRef, type RefObject } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowUp, Film, Shirt, Sparkles } from 'lucide-react';
import { CumbreMascot } from '../../../components/CumbreMascot';
// El contexto CHICO del chat: esta franja sólo necesita ABRIR el Agente dejándole el
// pedido escrito. Leer el grande (`useChatAtlas`) la haría re-renderizar con cada token
// del stream, y es una franja quieta en el medio de la home.
import { useChatAtlasCascaron } from '../../../chat-atlas/ChatAtlasContext';
import { RUTA_CHAT_ATLAS } from '../../../chat-atlas/ui/rutaChatAtlas';
import { trackEvent } from '../../../lib/posthog';
import { palabraDeModeloActual } from '../../../lib/nombreDeModelo';
import { esRubroBelleza, esRubroHogarDeco } from '../../../lib/rubro';

/** Con qué copy se dibuja la franja. Sale del rubro de la marca activa. */
type Habla = 'moda' | 'belleza' | 'hogar';

interface Pedido {
  key: string;
  label: string;
  Icon: typeof Shirt;
  prompt: string;
}

interface CopyDeRubro {
  /** La línea que acompaña a la mascota, arriba de la caja. */
  presentacion: string;
  /** Las ideas que se tipean solas adentro de la caja falsa. */
  ideas: readonly string[];
  /** Las pastillas de abajo. Son DOS: las dos cosas que el rubro viene a hacer acá. */
  pedidos: readonly Pedido[];
}

/**
 * El copy de cada rubro.
 *
 * Son **dos** pedidos y no cuatro: "Ideas para esta semana" y "Analizá tu competencia"
 * se sacaron porque la franja tiene que leerse de un vistazo —sobre todo en el teléfono,
 * donde cuatro pastillas ocupaban dos renglones enteros— y porque las dos que quedan son
 * lo que la marca viene a buscar a Atlas: fotos y video. Lo demás se lo puede pedir
 * igual escribiéndolo, que es justamente lo que la caja invita a hacer.
 *
 * El `prompt` completa la caja del Agente y NO se manda
 * (ADR-2026-09-12-las-sugerencias-completan-la-caja): lo que llega es una caja escrita,
 * que se puede editar antes de mandar.
 *
 * No son `SUGERENCIAS_AGENTE` —las del arranque del Agente y del hero de los otros
 * rubros— porque aquellas hablan de "mi producto" y valen para cualquier rubro. Acá la
 * franja le habla a un rubro concreto, y la diferencia entre "fotos de mi producto" y
 * "fotos de catálogo de una prenda, con fondo blanco, en modelo y de detalle" es
 * exactamente lo que hace que se toque: que el Agente le hable de SU trabajo.
 */
const COPY_POR_RUBRO: Record<Habla, CopyDeRubro> = {
  moda: {
    presentacion: 'Tu Empleado IA: pedile fotos, videos e ideas para tu marca, con tus prendas.',
    get ideas() {
      return [
        'Sacale fotos de catálogo a este vestido…',
        'Hacé un video de 10 segundos para Instagram…',
        `Probá esta remera en otro ${palabraDeModeloActual().singular}…`,
        'Dame ideas de contenido para esta semana…',
        'Armá una promo para el fin de semana…',
      ];
    },
    get pedidos() {
      return [
        {
          key: 'fotos',
          label: 'Fotos de tus prendas',
          Icon: Shirt,
          prompt: `Quiero fotos de catálogo de una prenda mía. Buscala en mi catálogo y proponeme tres tomas distintas (una con fondo blanco, una puesta en un ${palabraDeModeloActual().singular} y una de detalle de la tela) antes de generar nada.`,
        },
        {
          key: 'video',
          label: 'Un video para redes',
          Icon: Film,
          prompt:
            'Hacé un video corto de una de mis prendas para redes, vertical (9:16) y de unos 10 segundos, que arranque con un gancho fuerte y cierre con un llamado a la acción. Proponeme primero el guion toma por toma y después lo generamos.',
        },
      ];
    },
  },
  belleza: {
    presentacion: 'Tu Empleado IA: pedile fotos, videos e ideas para tu marca, con tus productos.',
    ideas: [
      'Sacale fotos de catálogo a esta crema…',
      'Hacé un video de 10 segundos para Instagram…',
      'Mostrá cómo se usa este producto…',
      'Dame ideas de contenido para esta semana…',
      'Armá una promo para el fin de semana…',
    ],
    pedidos: [
      {
        key: 'fotos',
        label: 'Fotos de tus productos',
        Icon: Sparkles,
        prompt:
          'Quiero fotos de catálogo de un producto mío. Buscalo en mi catálogo y proponeme tres tomas distintas (una con fondo limpio, una en uso y una de detalle de la textura) antes de generar nada.',
      },
      {
        key: 'video',
        label: 'Un video para redes',
        Icon: Film,
        prompt:
          'Hacé un video corto de uno de mis productos para redes, vertical (9:16) y de unos 10 segundos, que arranque con un gancho fuerte y cierre con un llamado a la acción. Proponeme primero el guion toma por toma y después lo generamos.',
      },
    ],
  },
  hogar: {
    presentacion: 'Tu Empleado IA: pedile fotos, videos e ideas para tu marca, con tus productos.',
    ideas: [
      'Sacale fotos de catálogo a este sillón…',
      'Hacé un video de 10 segundos para Instagram…',
      'Mostrá esta mesa en un living…',
      'Dame ideas de contenido para esta semana…',
      'Armá una promo para el fin de semana…',
    ],
    pedidos: [
      {
        key: 'fotos',
        label: 'Fotos de tus productos',
        Icon: Sparkles,
        prompt:
          'Quiero fotos de catálogo de un producto mío. Buscalo en mi catálogo y proponeme tres tomas distintas (una con fondo blanco, una ambientada en el espacio donde se usa y una de detalle del material) antes de generar nada.',
      },
      {
        key: 'video',
        label: 'Un video para redes',
        Icon: Film,
        prompt:
          'Hacé un video corto de uno de mis productos para redes, vertical (9:16) y de unos 10 segundos, que arranque con un gancho fuerte y cierre con un llamado a la acción. Proponeme primero el guion toma por toma y después lo generamos.',
      },
    ],
  },
};

/**
 * Con qué copy habla la franja.
 *
 * Los tres rubros que la ven son los que NO abren la home con el Agente
 * (`homeMuestraPresentacionDelAgente`), así que el `else` es Moda: el rubro que la
 * estrenó y el único que quedaba de los tres. Si mañana la ve un cuarto rubro, entra
 * acá con su copy y no cae en el de moda por descarte.
 */
export const hablaDelRubro = (rubro?: string | null): Habla =>
  esRubroBelleza(rubro) ? 'belleza' : esRubroHogarDeco(rubro) ? 'hogar' : 'moda';

// Ritmos del tipeo, en ms — los MISMOS de `placeholderQueSeEscribe`, que es el
// placeholder que se escribe solo en la caja del Agente. Están acá y no importados
// porque aquel hook escribe adentro del `textarea` de `BarraEntradaChat` (lo busca en el
// DOM) y esta barra no tiene `textarea`: es falsa a propósito.
const MS_ESCRIBIR = 38;
const MS_BORRAR = 16;
const MS_PAUSA_COMPLETA = 2300;
const MS_PAUSA_VACIA = 380;

/**
 * Escribe y borra `frases`, una por una, adentro de un elemento.
 *
 * Escribe `textContent` DERECHO sobre el nodo, sin `useState`: un estado que cambia cada
 * ~38ms re-renderizaría la franja entera veintipico de veces por segundo con la home
 * quieta. Es el mismo criterio —y el mismo porqué— que el placeholder de la caja.
 *
 * Con `prefers-reduced-motion` no se escribe nada: queda la primera frase, quieta.
 */
function useFraseQueSeEscribe(
  ref: RefObject<HTMLElement | null>,
  frases: readonly string[],
) {
  useEffect(() => {
    const el = ref.current;
    if (!el || frases.length === 0) return;
    const quieto =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (quieto) {
      el.textContent = frases[0];
      return;
    }
    let indice = 0;
    let largo = 0;
    let borrando = false;
    let temporizador = 0;
    const tic = () => {
      const frase = frases[indice % frases.length];
      if (!borrando) {
        largo += 1;
        el.textContent = frase.slice(0, largo);
        if (largo >= frase.length) {
          borrando = true;
          temporizador = window.setTimeout(tic, MS_PAUSA_COMPLETA);
          return;
        }
        temporizador = window.setTimeout(tic, MS_ESCRIBIR);
        return;
      }
      largo -= 1;
      el.textContent = frase.slice(0, largo);
      if (largo <= 0) {
        borrando = false;
        indice += 1;
        temporizador = window.setTimeout(tic, MS_PAUSA_VACIA);
        return;
      }
      temporizador = window.setTimeout(tic, MS_BORRAR);
    };
    el.textContent = '';
    temporizador = window.setTimeout(tic, MS_PAUSA_VACIA);
    return () => {
      window.clearTimeout(temporizador);
      // Se repone la primera frase: si el ciclo se corta a mitad de palabra, lo que
      // queda escrito en el DOM es medio pedido.
      el.textContent = frases[0];
    };
  }, [ref, frases]);
}

export interface PresentacionDelAgenteProps {
  /** El rubro de la MARCA ACTIVA: elige el copy (`hablaDelRubro`). */
  rubro?: string | null;
  /**
   * El plan no incluye el Agente (`getAgenteLock`, Pro y Ultra —
   * ADR-2026-09-14-el-agente-es-de-pro-y-ultra). La franja se sigue viendo —es
   * justamente lo que explica qué se está perdiendo— pero todo click va a los planes y
   * se dice ANTES de tocar.
   */
  bloqueado?: boolean;
  /** A dónde manda el click cuando el Agente está bloqueado. */
  onIrAPlanes: () => void;
  /**
   * Clases del `<section>`. Es cómo cada home le da su lugar: en escritorio la monta
   * `HomeMinimal` con el `mt-12` de las demás secciones, y en el teléfono `InterfazV2`
   * la cuelga debajo del carrusel con su propio margen y el `sm:hidden`.
   */
  className?: string;
}

/* ── LA BARRA ──────────────────────────────────────────────────────────────
   Una caja de texto FALSA del ancho de la home. No se puede escribir adentro: el
   click entero abre el Agente, que es donde está la caja de verdad —la misma que usa
   el chat, con adjuntos, inteligencia y dictado (`CajaDelAgenteHome`)—. Media caja
   funcionando acá arriba sería la segunda caja del producto, y la primera que no hace
   lo que la otra hace.

   El placeholder SE ESCRIBE SOLO: es lo que convierte a una caja vacía en una
   invitación, y lo que muestra CÓMO se le habla al agente y no sólo de qué. */
export function PresentacionDelAgente({
  rubro,
  bloqueado = false,
  onIrAPlanes,
  className = 'mt-12',
}: PresentacionDelAgenteProps) {
  const navigate = useNavigate();
  const { abrirChat } = useChatAtlasCascaron();
  const habla = hablaDelRubro(rubro);
  const { presentacion, ideas, pedidos } = COPY_POR_RUBRO[habla];
  const frase = useRef<HTMLSpanElement>(null);
  useFraseQueSeEscribe(frase, ideas);

  // Visto: una vez por montaje de la home, con el rubro que le tocó.
  useEffect(() => {
    trackEvent('agente_home_visto', { habla, bloqueado });
    // A propósito sólo `habla`: cambiar de marca a una de otro rubro es una vista nueva;
    // que el candado del plan llegue tarde no lo es.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [habla]);

  /**
   * Abrir el Agente, con el pedido ya escrito en su caja.
   *
   * `abrirChat` deja el `mensajeInicial` en el borrador —el MISMO que lee la caja del
   * chat— y `vista: 'pantalla'` evita que el panel lateral asome: React agrupa este
   * `setState` con el de `navigate`, así que el primer render que sale ya es el de
   * `/agente`, donde `useEmpujeChat` esconde el panel.
   */
  const probar = (origen: string, prompt?: string) => {
    trackEvent('agente_home_click', { habla, origen, bloqueado });
    if (bloqueado) {
      onIrAPlanes();
      return;
    }
    abrirChat({
      contexto: null,
      vista: 'pantalla',
      ...(prompt !== undefined ? { mensajeInicial: prompt } : {}),
    });
    navigate(RUTA_CHAT_ATLAS);
  };

  return (
    <section className={className}>
      <h2 className="text-[14px] font-semibold text-black/45 sm:text-[15px]">
        Probá el Agente
      </h2>

      {/* El panel. El degradé azulado es la ESTELA del Agente traída a una franja: la
          home de estos rubros no la lleva (es de la home que abre con el Agente), pero
          el color es lo que hace que esta franja se lea como "esto es el Agente" y no
          como una card más de la home.

          En el teléfono todo se achica un punto —el radio, el padding, la caja y las
          pastillas— para que la franja entre entera sin empujar hacia abajo lo que
          sigue: acá arriba ya está el carrusel de modos. */}
      <div className="relative mt-2.5 overflow-hidden rounded-2xl border border-[rgba(22,68,235,0.14)] bg-gradient-to-r from-[#F1F5FF] via-white to-[#F3FAFF] p-3.5 sm:mt-3 sm:rounded-3xl sm:p-5">
        <div className="flex items-center gap-2.5 sm:gap-3">
          {/* UNA sola mascota para los dos tamaños de pantalla: `.cumbre--mini` le fija
              el `display`, así que un `sm:hidden` de Tailwind no la esconde y se veían
              las dos (verificado en el navegador). 28px entra bien en los dos lados. */}
          <CumbreMascot size={28} decorativa className="cumbre--mini h-auto shrink-0" />
          <p className="text-[13px] leading-snug text-black/60 sm:text-[14px]">{presentacion}</p>
        </div>

        {/* LA CAJA FALSA. Es un `<button>` y no un `<div>` con `onClick`: el teclado
            tiene que llegar, y lo que pasa al tocarla es una navegación. */}
        <button
          type="button"
          onClick={() => probar('barra')}
          className="group mt-2.5 flex w-full items-center gap-2.5 rounded-xl bg-white px-3.5 py-3 text-left ring-1 ring-black/[0.08] transition-shadow hover:ring-[rgba(22,68,235,0.3)] hover:shadow-[0_6px_24px_rgba(16,24,45,0.07)] sm:mt-3 sm:gap-3 sm:rounded-2xl sm:px-4 sm:py-3.5"
        >
          <span className="min-w-0 flex-1 truncate text-[13.5px] text-black/40 sm:text-[15px]">
            {/* La frase arranca escrita en el HTML: es lo que se ve con el tipeo
                apagado (`prefers-reduced-motion`) y el primer cuadro antes de que el
                efecto tome el nodo. */}
            <span ref={frase}>{ideas[0]}</span>
            <span
              aria-hidden="true"
              // El azul va ESCRITO y no como `bg-brand-primary/70`: los tokens `brand-*`
              // son variables CSS sin `<alpha-value>`, así que el modificador de opacidad
              // no emite regla y el cursor quedaba transparente (verificado en el
              // navegador: `backgroundColor` = `rgba(0,0,0,0)`).
              className="ml-[3px] inline-block h-[14px] w-[2px] translate-y-[2px] animate-pulse rounded-full bg-[rgba(22,68,235,0.7)] sm:h-[15px] sm:translate-y-[3px]"
            />
          </span>
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-brand-primary text-white transition-transform duration-300 group-hover:scale-105 sm:h-9 sm:w-9">
            <ArrowUp className="h-4 w-4 sm:h-[18px] sm:w-[18px]" />
          </span>
        </button>

        {/* Los pedidos, abajo de la caja y del mismo azul clarito que las sugerencias
            del Agente: son las mismas pastillas, en la misma posición. Acá no completan
            una caja —no hay— sino que abren el Agente con el pedido ya escrito.

            En el teléfono el rótulo va EN UNA SOLA LÍNEA y la fila SE CORRE a la derecha
            —ADR-2026-09-24-las-pastillas-del-agente-se-corren, supersede
            ADR-2026-09-22-las-dos-pastillas-del-agente-comparten-la-fila. Antes cada una
            tomaba mitad de ancho (`flex-1 basis-0`) y el rótulo se partía en dos
            renglones, porque a 386px las dos en una línea suman 380px contra 316px de
            fila. Partirlas entraba, pero un pedido de cuatro palabras cortado al medio se
            lee peor que uno que se sale de la pantalla: la tira que se corre es la misma
            solución que ya usa la fila de fuentes de la caja
            (ADR-2026-09-22-en-el-telefono-la-fila-de-fuentes-se-corre), y la segunda
            pastilla asomando es lo que avisa que hay más.

            La fila se sale del padding de la franja (`-mx-3.5 px-3.5`) para que lo que se
            corre llegue hasta el borde en vez de frenar contra el aire de la tarjeta; el
            `overflow-hidden` de la franja la recorta ahí. De `sm` para arriba no se corre
            nada: vuelven a ser dos pastillas al ancho de su texto, que ahí entran. */}
        <div className="scrollbar-hide -mx-3.5 mt-2.5 flex gap-2 overflow-x-auto px-3.5 sm:mx-0 sm:mt-3 sm:flex-wrap sm:overflow-x-visible sm:px-0">
          {pedidos.map(({ key, label, prompt, Icon }) => (
            <button
              key={key}
              type="button"
              onClick={() => probar(`barra:${key}`, prompt)}
              className="inline-flex shrink-0 items-center gap-2 rounded-full bg-[#E9EDFD] px-3.5 py-2.5 text-left text-[13px] font-semibold text-brand-primary transition-colors hover:bg-[#D8E0FB] sm:px-4 sm:py-2"
            >
              <Icon className="h-4 w-4 shrink-0" strokeWidth={2.25} />
              <span className="whitespace-nowrap">{label}</span>
            </button>
          ))}
        </div>

        {/* La nota del plan. Va abajo de todo y en chico: se dice antes de tocar, pero
            no es lo que la franja viene a contar. */}
        {bloqueado && (
          <p className="mt-2.5 text-[12.5px] leading-snug text-black/45">
            El Agente viene con los planes Pro y Ultra
          </p>
        )}
      </div>
    </section>
  );
}

export default PresentacionDelAgente;
